/**
 * Sponsor dashboard session — stores the signed Studio sponsor token
 * (issued by /api/studio/sponsor-login) and wraps fetches to the sponsor
 * proxies. Exact mirror of customerAuth.ts's pattern for a different
 * principal (docs/specs/sponsor-enrollment-engine-plan.md §8 step 8).
 */

const TOKEN_KEY = 'studio_sponsor_token'
const ORG_NAME_KEY = 'studio_sponsor_org_name'

export function getSponsorToken(): string | null {
	if (typeof window === 'undefined') return null
	return window.localStorage.getItem(TOKEN_KEY)
}

export function setSponsorToken(token: string): void {
	window.localStorage.setItem(TOKEN_KEY, token)
}

export function clearSponsorToken(): void {
	window.localStorage.removeItem(TOKEN_KEY)
	window.localStorage.removeItem(ORG_NAME_KEY)
}

/** organization_name is only ever returned once, in the sponsor-login
 * response body — there's no re-fetchable "me" endpoint for it — so it's
 * cached alongside the token for the Home page's org info bar. */
export function getSponsorOrgName(): string | null {
	if (typeof window === 'undefined') return null
	return window.localStorage.getItem(ORG_NAME_KEY)
}

export function setSponsorOrgName(name: string): void {
	window.localStorage.setItem(ORG_NAME_KEY, name)
}

export class SponsorSessionExpired extends Error {}

/** GET a sponsor proxy with the sponsor bearer token. Throws
 * SponsorSessionExpired on 401 — callers clear the token and redirect to
 * /sponsor/login. */
export async function fetchWithSponsorAuth<T>(path: string): Promise<T> {
	const token = getSponsorToken()
	if (!token) throw new SponsorSessionExpired('No session')
	const res = await fetch(path, {
		headers: { Authorization: `Bearer ${token}` },
	})
	if (res.status === 401) {
		clearSponsorToken()
		throw new SponsorSessionExpired('Session expired')
	}
	if (!res.ok) {
		throw new Error(`${path} failed: ${res.status}`)
	}
	return (await res.json()) as T
}

/** POST a sponsor proxy with the sponsor bearer token. Same session
 * semantics as fetchWithSponsorAuth. */
export async function postWithSponsorAuth<T>(
	path: string,
	body: unknown
): Promise<T> {
	const token = getSponsorToken()
	if (!token) throw new SponsorSessionExpired('No session')
	const res = await fetch(path, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	})
	if (res.status === 401) {
		clearSponsorToken()
		throw new SponsorSessionExpired('Session expired')
	}
	if (!res.ok) {
		const errBody = await res.json().catch(() => ({}))
		throw new Error(
			(errBody as { detail?: string }).detail ?? `${path} failed: ${res.status}`
		)
	}
	return (await res.json()) as T
}

/** Open a sponsor proxy's response (an HTML document — the quote/invoice
 * "PDF" is server-rendered HTML, meant to be viewed or printed) in a new
 * tab, with the sponsor bearer token attached. A plain `<a href target
 * ="_blank">` can't do this — it can't set the Authorization header the
 * proxy requires, so it always 401s. Opens the tab synchronously (before
 * the fetch resolves) so browsers don't treat it as a blocked popup. */
export async function openWithSponsorAuth(path: string): Promise<void> {
	const token = getSponsorToken()
	if (!token) throw new SponsorSessionExpired('No session')
	const newTab = window.open('', '_blank')
	try {
		const res = await fetch(path, {
			headers: { Authorization: `Bearer ${token}` },
		})
		if (res.status === 401) {
			clearSponsorToken()
			throw new SponsorSessionExpired('Session expired')
		}
		if (!res.ok) {
			throw new Error(`${path} failed: ${res.status}`)
		}
		const blob = await res.blob()
		const url = URL.createObjectURL(blob)
		if (newTab) newTab.location.href = url
		else window.location.href = url
	} catch (e) {
		newTab?.close()
		throw e
	}
}

/** Force-download a sponsor proxy's response (a raw file — e.g. the roster
 * CSV template — as opposed to openWithSponsorAuth's viewable HTML) with
 * the sponsor bearer token attached, since a plain `<a href>` can't set
 * that header either. Reads the filename Studio set via
 * Content-Disposition when present, falling back otherwise. */
export async function downloadWithSponsorAuth(
	path: string,
	fallbackFilename: string
): Promise<void> {
	const token = getSponsorToken()
	if (!token) throw new SponsorSessionExpired('No session')
	const res = await fetch(path, {
		headers: { Authorization: `Bearer ${token}` },
	})
	if (res.status === 401) {
		clearSponsorToken()
		throw new SponsorSessionExpired('Session expired')
	}
	if (!res.ok) {
		throw new Error(`${path} failed: ${res.status}`)
	}
	const disposition = res.headers.get('content-disposition') || ''
	const match = disposition.match(/filename="?([^";]+)"?/)
	const filename = match?.[1] || fallbackFilename
	const blob = await res.blob()
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	link.remove()
	URL.revokeObjectURL(url)
}

/** PATCH a sponsor proxy with the sponsor bearer token. Same session
 * semantics as fetchWithSponsorAuth/postWithSponsorAuth. */
export async function patchWithSponsorAuth<T>(
	path: string,
	body: unknown
): Promise<T> {
	const token = getSponsorToken()
	if (!token) throw new SponsorSessionExpired('No session')
	const res = await fetch(path, {
		method: 'PATCH',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	})
	if (res.status === 401) {
		clearSponsorToken()
		throw new SponsorSessionExpired('Session expired')
	}
	if (!res.ok) {
		const errBody = await res.json().catch(() => ({}))
		throw new Error(
			(errBody as { detail?: string }).detail ?? `${path} failed: ${res.status}`
		)
	}
	return (await res.json()) as T
}
